<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import fileTextMultipleIcon from '@/shared/assets/icons/FileTextMultiple.svg';
  import { useDashboardStore } from '../../model/dashboard.store';
  import { formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import VariationBadge from '../VariationBadge.vue';
  import VerticalBars, { type VerticalBarItem } from '../VerticalBars.vue';
  import WidgetCard from '../WidgetCard.vue';

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  const items = computed<readonly VerticalBarItem[]>(() => data.value?.documentTypes.items ?? []);

  const hasData = computed(() => items.value.some((item) => item.value > 0));

  const variationPct = computed(() =>
    hasData.value ? (data.value?.documentTypes.variationPct ?? null) : null
  );

  const badgeText = computed(() => {
    if (variationPct.value === null || variationPct.value === 0) return undefined;
    const direction = variationPct.value > 0 ? 'mais' : 'menos';
    return `${formatVariationPct(variationPct.value)} ${direction} documentos`;
  });
</script>

<template>
  <WidgetCard
    title="Tipos de documentos"
    :icon="fileTextMultipleIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="vertical-bars"
  >
    <div
      data-testid="document-types-widget"
      class="flex h-full flex-col gap-5"
    >
      <div class="flex min-h-0 flex-1 items-center">
        <p
          v-if="items.length === 0"
          data-testid="document-types-empty"
          class="w-full text-center text-sm text-gray-03"
        >
          Nenhum documento no período
        </p>
        <VerticalBars
          v-else
          class="h-full w-full"
          :items="items"
        />
      </div>
      <VariationBadge
        class="self-center"
        :variation-pct="variationPct"
        :text="badgeText"
        :comparison-label="comparisonLabel"
      />
    </div>
  </WidgetCard>
</template>
